import { canv } from "@/canv/global";
import type { ExtendsOfElementBase } from "@/canvasController/types";
import type { TableWidgetCell } from "@/elements/table/tableCell";
import { Group } from "@/group";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import type { ArrowEndOption, ArrowEndType, BorderOption, LineOption } from "../types";

interface applyValueArgs {
    options: widgetOption[];
    value: string | number | boolean | object | widgetOption<TableWidgetCell>;
    setNewOptions: (options: widgetOption[]) => void;
    handleClose?: () => void;
    makeEvent?: boolean;
}

function getReasonForWidget(name: string) {
    switch (name) {
        case "arrowEndFirst":
            return "wm_arrowEnd";

        default:
            return `wm_${name}`;
    }
}

function setOptionValue(
    option: widgetOption,
    value: string | number | boolean | object | ArrowEndOption | LineOption | BorderOption,
) {
    switch (option.name) {
        case "border":
        case "lineType":
            option.value = {
                ...(option.value as LineOption | BorderOption),
                ...(value as LineOption | BorderOption),
            };
            break;

        case "arrowEnd":
        case "arrowEndFirst":
            (option as widgetOption<ArrowEndType>).value = (value as ArrowEndOption).endType;
            break;

        default:
            option.value = value;
    }
}

export async function applyValue({
    options,
    value,
    setNewOptions,
    handleClose,
    makeEvent = true,
}: applyValueArgs) {
    const settedUIDs: string[] = [];
    let hasAppliedValue = false;

    if (makeEvent) {
        canv.eventManager.openRecord();
    }

    for (let i = 0; i < options.length; i++) {
        const index = settedUIDs.indexOf(options[i].UID);

        const widget = canv.canvasController.FindByUID(options[i].UID);

        const isTableColorChange =
            widget &&
            (widget as ExtendsOfElementBase).constructorName === "table" &&
            options[i].name === "color";

        if (isTableColorChange) {
            setOptionValue(options[i], value);
        }

        if (index !== -1) {
            continue; // если уже сетили такой виджет. Такое может быть. Например таблица может вернуть несколько цветов (разные цвета у разных ячеек)
        }

        if (!widget) {
            continue;
        }
        if (widget instanceof Group) {
            return;
        }

        settedUIDs.push(options[i].UID);

        const handler = widget.getHandlerForOption(options[i].name);

        if (!handler) {
            continue;
        }

        const bindedFunc = handler.bind(widget);
        const reason = getReasonForWidget(options[i].name);

        if (makeEvent && widget.parent) {
            (widget.parent as Group).fixWidgets(reason);
        }

        const handlerResult = await bindedFunc(value);
        if (handlerResult === false) {
            continue;
        }

        setOptionValue(options[i], value);
        hasAppliedValue = true;

        if (makeEvent && widget.parent) {
            (widget.parent as Group).saveWidgets(reason);
        }
    }

    const newOptions = [...options];
    setNewOptions(newOptions);

    if (makeEvent) {
        canv.eventManager.closeRecord();
    }
    if (handleClose) {
        handleClose();
    }

    canv.redraw();

    return hasAppliedValue;
}
